import type { PageDefinition } from '@/types/page'
import {
  FAMILY_RISK,
  FAMILY_ROLE_OPTIONS,
  FAMILY_STATUS,
  FAMILY_TYPE_OPTIONS,
  GENDER_OPTIONS,
  REGION_STATUS,
  USER_AGE_OPTIONS,
  USER_STATUS,
  toOptions,
} from './common'

export const usersPage: PageDefinition = {
  id: 'users',
  route: '#/users',
  title: '用户管理',
  purpose: '管理平台用户、家庭成员关系、慢病标签、设备绑定与健康风险摘要',
  group: '用户管理',
  icon: 'users',
  kind: 'table',
  permission: 'users.view',
  permissions: {
    create: 'users.create',
    edit: 'users.edit',
    status: 'users.status',
    export: 'users.export',
    tag: 'users.tag',
  },
  searchKeys: ['name', 'phone', 'user_no', 'family_name', 'address', 'region_name'],
  statusKey: 'status',
  statusMap: USER_STATUS,
  timeField: 'registered_at',
  summary: [
    { key: 'total', label: '用户总数', kind: 'count', hint: '全部已建档用户' },
    { key: 'active', label: '正常用户', kind: 'count', where: { status: 'active' }, hint: '可正常登录与接收提醒' },
    { key: 'senior', label: '60岁以上', kind: 'count', where: { age_gte: 60 }, hint: '按出生日期计算' },
    { key: 'risk', label: '高风险用户', kind: 'count', where: { risk_level: '高风险' }, hint: '需重点随访' },
    { key: 'new', label: '本月新增', kind: 'count', where: { registered_within_days: 30 }, hint: '近30天注册' },
    { key: 'pending', label: '待认证', kind: 'count', where: { status: 'pending_verification' }, hint: '等待身份核验' },
  ],
  columns: [
    { key: 'name', label: '用户', type: 'primary', subtitleKey: 'user_no', minWidth: 142, sortable: true },
    { key: 'phone', label: '手机号', type: 'phone', width: 124 },
    { key: 'gender', label: '性别', width: 62, options: GENDER_OPTIONS },
    { key: 'age', label: '年龄', type: 'number', width: 68, align: 'right', suffix: ' 岁' },
    { key: 'region_name', label: '所属区域', minWidth: 96 },
    { key: 'family_name', label: '家庭', minWidth: 86, subtitleKey: 'family_role' },
    { key: 'tags', label: '健康标签', type: 'tag', minWidth: 126, hideByDefault: true },
    { key: 'risk_level', label: '风险等级', type: 'status', width: 84, options: toOptions(FAMILY_RISK) },
    { key: 'device_bound', label: '设备绑定', type: 'boolean', width: 78, align: 'center', hideBelow: 1120 },
    { key: 'status', label: '账号状态', type: 'status', width: 84 },
    { key: 'last_active_at', label: '最近活跃', type: 'datetime', width: 140, sortable: true, hideBelow: 1300 },
    { key: 'registered_at', label: '注册时间', type: 'datetime', width: 148, sortable: true, hideByDefault: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '姓名、手机号、家庭或地址', width: 230 },
    { key: 'status', label: '账号状态', type: 'select', options: toOptions(USER_STATUS), width: 128 },
    { key: 'age_group', label: '年龄段', type: 'select', options: USER_AGE_OPTIONS, width: 128 },
    { key: 'family_type', label: '家庭类型', type: 'select', options: FAMILY_TYPE_OPTIONS, width: 128 },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'device_bound', label: '设备绑定', type: 'select', options: [{ value: 'true', label: '已绑定' }, { value: 'false', label: '未绑定' }], width: 112 },
    { key: 'registered_at', label: '注册时间', type: 'daterange', timeField: 'registered_at', width: 230 },
  ],
  formFields: [
    { key: 'user_no', label: '用户编号', type: 'readonly', readonlyOnEdit: true, help: '保存后由系统生成，不可修改', placeholder: '保存后自动生成' },
    { key: 'name', label: '姓名', type: 'text', required: true, maxLength: 20, placeholder: '请输入真实姓名' },
    { key: 'phone', label: '手机号', type: 'phone', required: true, pattern: '^1[3-9]\\d{9}$', patternMessage: '请输入11位有效手机号', placeholder: '用于登录与提醒' },
    { key: 'birth_date', label: '出生日期', type: 'date', required: true, help: '年龄段由出生日期自动计算' },
    { key: 'gender', label: '性别', type: 'select', options: GENDER_OPTIONS, required: true },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', required: true },
    { key: 'family_id', label: '所属家庭', type: 'select', optionSource: 'families', help: '留空表示尚未加入家庭' },
    { key: 'family_role', label: '家庭角色', type: 'select', options: FAMILY_ROLE_OPTIONS },
    { key: 'tags', label: '健康标签', type: 'multiselect', optionSource: 'user_tags', help: '同步到家庭档案与消息受众' },
    { key: 'status', label: '账号状态', type: 'select', options: toOptions(USER_STATUS), defaultValue: 'active' },
    { key: 'address', label: '联系地址', type: 'textarea', full: true, maxLength: 120, placeholder: '请输入省市区与详细门牌' },
    { key: 'emergency_contact', label: '紧急联系人', type: 'text', maxLength: 40 },
    { key: 'height_cm', label: '身高', type: 'number', min: 80, max: 220, suffix: 'cm' },
    { key: 'weight_kg', label: '体重', type: 'number', min: 25, max: 200, suffix: 'kg' },
    { key: 'blood_type', label: '血型', type: 'select', options: ['A型', 'B型', 'O型', 'AB型'].map(value => ({ value, label: value })) },
    { key: 'medical_history', label: '既往病史', type: 'textarea', full: true, maxLength: 300 },
    { key: 'allergy_history', label: '过敏史', type: 'textarea', full: true, maxLength: 200 },
  ],
  createLabel: '新增用户',
  createPermission: 'users.create',
  editPermission: 'users.edit',
  deletePermission: 'users.status',
  batchDeletePermission: 'users.status',
  exportPermission: 'users.export',
  exportScope: 'current-page',
  extraActions: [
    { key: 'import-users', label: '导入档案', icon: 'upload', kind: 'import', permission: 'users.create' },
    { key: 'tags', label: '健康标签', icon: 'tag', kind: 'link', to: '#/users?panel=tags', permission: 'users.tag', overflowOnly: true },
    { key: 'privacy', label: '隐私与授权', icon: 'shield', kind: 'link', to: '#/users?tab=privacy', permission: 'users.view', overflowOnly: true },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'users.edit', tone: 'primary' },
    { key: 'tags', label: '健康标签', permission: 'users.tag' },
    { key: 'bind-device', label: '绑定设备', permission: 'devices.bind' },
    {
      key: 'status-toggle',
      label: '停用',
      permission: 'users.status',
      tone: 'danger',
      confirm: true,
      form: [{ key: 'reason', label: '停用原因', type: 'textarea', required: true, full: true, placeholder: '停用后用户端会话立即失效，请填写原因' }],
      visibleWhen: { field: 'status', in: ['active', 'pending_verification'] },
    },
    {
      key: 'status-toggle',
      label: '启用',
      permission: 'users.status',
      tone: 'primary',
      confirm: true,
      visibleWhen: { field: 'status', in: ['disabled'] },
    },
    {
      key: 'cancel-account',
      label: '注销',
      permission: 'users.status',
      tone: 'danger',
      confirm: true,
      confirmText: '注销仅允许无未完成订单、无在办工单的用户；健康数据按法定期限保留。',
      form: [{ key: 'reason', label: '注销原因', type: 'textarea', required: true, full: true }],
      visibleWhen: { field: 'status', in: ['active', 'pending_verification', 'disabled'] },
    },
  ],
  batchActions: [
    {
      key: 'batch-disable',
      label: '批量停用',
      permission: 'users.status',
      tone: 'danger',
      confirm: true,
      kind: 'transition',
      form: [{ key: 'reason', label: '批量停用原因', type: 'textarea', required: true, full: true }],
    },
    { key: 'batch-export', label: '导出当前页', permission: 'users.export', kind: 'export' },
  ],
  detailSections: [
    { title: '账号资料', fields: ['user_no', 'name', 'phone', 'gender', 'age', 'region_name', 'address', 'status', 'consent_scope'] },
    { title: '健康档案', fields: ['height_cm', 'weight_kg', 'blood_type', 'medical_history', 'allergy_history', 'tags', 'risk_level'] },
    { title: '家庭与守护', fields: ['family_name', 'family_role', 'emergency_contact', 'device_bound', 'last_active_at', 'registered_at'] },
  ],
  detailMetrics: [
    { label: '用药依从率', field: 'adherence', type: 'percent' },
    { label: '未处理预警', field: 'open_alerts', type: 'number' },
    { label: '绑定设备', field: 'device_count', type: 'number' },
  ],
  notice: '手机号默认脱敏展示；停用账号会立即失效用户端会话，注销需满足订单与工单限制。',
  emptyHint: '没有符合当前筛选条件的用户，可重置筛选后重试。',
  numberPrefix: 'KH',
  seedKey: 'users',
}

export const familiesPage: PageDefinition = {
  id: 'families',
  route: '#/families',
  title: '家庭档案',
  purpose: '维护家庭关系、守护人、区域地址与风险等级，支撑家庭维度的健康服务',
  group: '家庭档案',
  icon: 'family',
  kind: 'table',
  permission: 'families.view',
  permissions: {
    create: 'families.edit',
    edit: 'families.edit',
    member: 'families.member',
    risk: 'families.risk',
    export: 'families.export',
  },
  searchKeys: ['name', 'family_no', 'owner_name', 'address', 'region_name'],
  statusKey: 'status',
  statusMap: FAMILY_STATUS,
  summary: [
    { key: 'total', label: '家庭总数', kind: 'count' },
    { key: 'active', label: '正常家庭', kind: 'count', where: { status: 'active' } },
    { key: 'high', label: '高风险家庭', kind: 'count', where: { risk_level: '高风险' } },
    { key: 'senior', label: '独居长者家庭', kind: 'count', where: { family_type: '独居长者' } },
    { key: 'members', label: '家庭成员', kind: 'sum', field: 'member_count', suffix: ' 人' },
  ],
  columns: [
    { key: 'name', label: '家庭', type: 'primary', subtitleKey: 'family_no', minWidth: 120, sortable: true },
    { key: 'owner_name', label: '家庭创建人', minWidth: 100 },
    { key: 'family_type', label: '家庭类型', minWidth: 100 },
    { key: 'region_name', label: '所属区域', minWidth: 100 },
    { key: 'address', label: '联系地址', minWidth: 190 },
    { key: 'member_count', label: '成员数', type: 'number', width: 76, align: 'right', suffix: ' 人' },
    { key: 'guardian_count', label: '守护人', type: 'number', width: 76, align: 'right', suffix: ' 人' },
    { key: 'risk_level', label: '风险等级', type: 'status', width: 84, options: toOptions(FAMILY_RISK) },
    { key: 'status', label: '状态', type: 'status', width: 82 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '家庭名称、编号或创建人', width: 220 },
    { key: 'risk_level', label: '风险等级', type: 'select', options: toOptions(FAMILY_RISK), width: 118 },
    { key: 'family_type', label: '家庭类型', type: 'select', options: FAMILY_TYPE_OPTIONS, width: 122 },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(FAMILY_STATUS), width: 104 },
  ],
  formFields: [
    { key: 'family_no', label: '家庭编号', type: 'readonly', readonlyOnEdit: true, help: '保存后自动生成' },
    { key: 'name', label: '家庭名称', type: 'text', required: true, maxLength: 30 },
    { key: 'owner_user_id', label: '家庭创建人', type: 'select', optionSource: 'users', required: true },
    { key: 'family_type', label: '家庭类型', type: 'select', options: FAMILY_TYPE_OPTIONS, required: true },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', required: true },
    { key: 'address', label: '联系地址', type: 'textarea', required: true, full: true, maxLength: 120 },
    { key: 'risk_level', label: '风险等级', type: 'select', options: toOptions(FAMILY_RISK), defaultValue: '低风险' },
    { key: 'status', label: '状态', type: 'select', options: toOptions(FAMILY_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增家庭',
  createPermission: 'families.edit',
  editPermission: 'families.edit',
  deletePermission: 'families.edit',
  exportPermission: 'families.export',
  exportScope: 'current-page',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'families.edit', tone: 'primary' },
    { key: 'add-member', label: '新增成员', permission: 'families.member' },
    { key: 'remove-member', label: '移除成员', permission: 'families.member', tone: 'danger', confirm: true },
    { key: 'risk-recompute', label: '重算风险', permission: 'families.risk' },
    {
      key: 'migrate-region',
      label: '迁移区域',
      permission: 'families.edit',
      form: [{ key: 'region_id', label: '目标区域', type: 'region', optionSource: 'regions', required: true }],
      confirm: true,
    },
  ],
  batchActions: [{ key: 'batch-export', label: '导出当前页', permission: 'families.export', kind: 'export' }],
  detailSections: [
    { title: '家庭信息', fields: ['family_no', 'name', 'owner_name', 'family_type', 'region_name', 'address', 'risk_level', 'status'] },
    { title: '守护关系', fields: ['member_count', 'guardian_count', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '家庭成员', field: 'member_count', type: 'number', suffix: ' 人' },
    { label: '守护人', field: 'guardian_count', type: 'number', suffix: ' 人' },
    { label: '未处理预警', field: 'open_alerts', type: 'number' },
  ],
  notice: '移除最后一名家庭守护人会被拒绝；区域迁移会同步家庭地址与设备归属。',
  emptyHint: '当前筛选下没有家庭档案。',
  numberPrefix: 'JT',
  seedKey: 'families',
}

export const regionsPage: PageDefinition = {
  id: 'regions',
  route: '#/regions',
  title: '区域与地址',
  purpose: '维护省市区主数据、启用状态与关联资源，为数据范围提供统一区域口径',
  group: '用户管理',
  icon: 'region',
  kind: 'tree-table',
  permission: 'regions.view',
  permissions: { create: 'regions.manage', edit: 'regions.manage', disable: 'regions.manage', migrate: 'regions.manage' },
  searchKeys: ['name', 'code', 'path'],
  statusKey: 'status',
  statusMap: REGION_STATUS,
  summary: [
    { key: 'total', label: '区域节点', kind: 'count' },
    { key: 'district', label: '区县级', kind: 'count', where: { level: 'district' } },
    { key: 'resources', label: '关联资源', kind: 'sum', field: 'resource_count', suffix: ' 项' },
    { key: 'scopes', label: '数据范围', kind: 'sum', field: 'admin_scope_count', suffix: ' 个' },
  ],
  columns: [
    { key: 'name', label: '区域名称', type: 'primary', subtitleKey: 'path', minWidth: 220, sortable: true },
    { key: 'code', label: '行政编码', width: 110 },
    { key: 'level_label', label: '层级', width: 86 },
    { key: 'parent_name', label: '上级区域', minWidth: 100 },
    { key: 'resource_count', label: '关联资源', type: 'number', width: 96, align: 'right', suffix: ' 项' },
    { key: 'admin_scope_count', label: '数据范围', type: 'number', width: 96, align: 'right', suffix: ' 个' },
    { key: 'status', label: '状态', type: 'status', width: 84 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '区域名称或行政编码', width: 220 },
    { key: 'level', label: '层级', type: 'select', options: [{ value: 'province', label: '省级' }, { value: 'city', label: '市级' }, { value: 'district', label: '区县级' }], width: 112 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(REGION_STATUS), width: 104 },
  ],
  formFields: [
    { key: 'name', label: '区域名称', type: 'text', required: true, maxLength: 30 },
    { key: 'code', label: '行政编码', type: 'text', required: true, pattern: '^\\d{6}$', patternMessage: '请输入6位行政编码', readonlyOnEdit: true },
    { key: 'parent_id', label: '上级区域', type: 'select', optionSource: 'regions' },
    { key: 'level', label: '层级', type: 'select', options: [{ value: 'province', label: '省级' }, { value: 'city', label: '市级' }, { value: 'district', label: '区县级' }], required: true },
    { key: 'sort', label: '排序', type: 'number', min: 1, max: 999, defaultValue: 1 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(REGION_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增区域',
  createPermission: 'regions.manage',
  editPermission: 'regions.manage',
  deletePermission: 'regions.manage',
  exportScope: 'none',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'regions.manage', tone: 'primary' },
    { key: 'add-child', label: '添加下级', permission: 'regions.manage' },
    {
      key: 'disable-region',
      label: '停用',
      permission: 'regions.manage',
      tone: 'danger',
      confirm: true,
      confirmText: '区域仍关联用户、家庭、设备或管理员数据范围时禁止停用，必须先完成资源迁移。',
      form: [{ key: 'reason', label: '停用原因', type: 'textarea', required: true, full: true }],
      visibleWhen: { field: 'status', in: ['active'] },
    },
    { key: 'enable-region', label: '启用', permission: 'regions.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
    { key: 'migrate-region', label: '迁移资源', permission: 'regions.manage', form: [{ key: 'target_region_id', label: '目标区域', type: 'select', optionSource: 'regions', required: true }], confirm: true },
  ],
  detailSections: [
    { title: '区域信息', fields: ['name', 'code', 'level_label', 'parent_name', 'path', 'sort', 'status'] },
    { title: '资源引用', fields: ['resource_count', 'resource_summary', 'admin_scope_count', 'created_at', 'updated_at'] },
  ],
  notice: '停用区域前必须迁移全部关联用户、家庭、设备与管理员数据范围。',
  emptyHint: '没有匹配的区域节点。',
  seedKey: 'regions',
}
